<template>
  <!-- While loading, the server-rendered #enquiry-form-static shell (with its loading placeholder) acts as the loader -->
  <div v-if="isLoading" />
  <div
    v-else
    data-test-id="enquiry-form-container"
  >
    <teleport
      :to="attachFormTarget"
    >
      <!-- Below 1280px the tiles sit in the page column, where at full width the buttons stretched across the page -->
      <div
        ref="enquiryForm"
        data-test-id="enquiry-form__wrapper"
        :class="{ 'sm:grid sm:grid-cols-2 sm:gap-x-4': !isBigDesktop }"
      >
        <PageTile
          border-type="shadow"
          class="mt-4 mb-4"
        >
          <template v-if="isBigDesktop">
            <EnquiryFormHeader
              v-if="!$store.getters.isRtbFlowPossible
                || (($store.state.step === $store.state.STEPS.messageAndCatering
                  || $store.state.step === $store.state.STEPS.userInfo) && !$store.state.requestToBookPath)"
              class="mb-4 justify-center md:justify-start"
            />
            <EnquiryPricingBar
              v-if="$store.getters.isRtbFlowPossible
                && !$store.state.user.lastEnquiry.date
                && !$store.state.showSuspensionWarning
                && !$store.state.leadHubOffer
                && (($store.state.step !== $store.state.STEPS.messageAndCatering
                  && $store.state.step !== $store.state.STEPS.userInfo))"
            />

            <div v-if="$store.state.showSuspensionWarning && !$store.state.user.lastEnquiry.date">
              <SuspensionWarning :class="{'mt-6': !$store.getters.isRtbFlowPossible}" />
              <EnquiryFormBottom class="mt-8" />
            </div>

            <div v-else-if="$store.state.user.lastEnquiry.date">
              <AlreadyEnquired />
              <EnquiryFormBottom :class="{'mt-8': !$store.getters.isRtbFlowPossible, 'mt-4': $store.getters.isRtbFlowPossible}" />
            </div>

            <div v-else-if="$store.state.leadHubOffer">
              <HasLeadHubOffer
                class="my-6"
              />
              <EnquiryFormBottom class="mt-8" />
            </div>

            <div v-else-if="$store.getters.isRtbFlowPossible">
              <EnquiryForm @openModal="(data) => openModal(data)" />
            </div>

            <div v-else>
              <EnquiryForm />
              <EnquiryFormBottom class="mt-8" />
            </div>
          </template>
          <template v-else>
            <EnquiryFormHeader
              v-if="!$store.getters.isRtbFlowPossible"
              class="justify-center md:justify-start mb-4"
            />
            <EnquiryPricingBar v-if="$store.getters.isRtbFlowPossible" />
            <MobileStartEnquiryBtn
              @click="openModal({ requestToBook: true, requestToBookMobilePath: true })"
            />
            <div
              v-if="$store.getters.isRtbFlowPossible"
              class="text-grey-600 text-xs flex items-center justify-center mt-4"
            >
              You won't be charged yet
            </div>
          </template>
        </PageTile>

        <PageTile
          v-if="$store.getters.isRtbFlowPossible
            && !$store.state.user.lastEnquiry?.date
            && !$store.state.leadHubOffer
            && !$store.state.showSuspensionWarning"
          border-type="shadow"
          class="mt-4 mb-4 flex flex-col"
        >
          <EnquiryFormHeader
            class="mb-4 justify-center md:justify-start"
          />
          <BaseButton
            color="white"
            class="w-full flex items-center justify-center mt-auto"
            data-test-id="message-host-btn"
            @click="messageHostBtnClicked"
          >
            Message host
          </BaseButton>
        </PageTile>

        <AddToFavourites v-if="!$store.state.isVenueSuspended" class="col-start-1" />

        <EnquiryFormModal
          v-if="$store.state.isEnquiryFormModalOpen"
          @close="$store.commit('setIsEnquiryFormModalOpen', false)"
        />
      </div>

      <MobileBottomButton
        v-if="!$store.state.user.lastEnquiry.date"
        :is-visible="showMobileButtons"
        @click="openModal({ requestToBook: true, requestToBookMobilePath: true })"
      />
    </teleport>

    <teleport to="#enquiry-sent-modal">
      <EnquirySentModal :config="$store.state.enquirySentModalConfig" />
      <EnquirySentWelcomeBackModal v-if="$store.state.enquirySentWelcomeBack" />

      <ReviewModal
        :venue-name="$store.state.venue.name"
        :venue-id="$store.state.venue.id"
        @track="onTrackReviewsModal"
      />
      <MapModal :map-config="$store.state.map" :title="`${$store.state.room.name} at ${$store.state.venue.name}`" />
      <RoomAddOnModal />
      <RoomMenuModal />
      <PricingDetailsModal />
    </teleport>

    <WishlistModals />
  </div>
</template>

<script>
import ScreenWidth from '@/components/ScreenWidth.js';
import PageTile from '@/components/TailwindComponents/PageTile.vue';
import BaseButton from '@/components/Ui/Buttons/BaseButton.vue';
import { setContextParam } from '@/services/context-store.js';
import ElementVisibility from '@/services/elementVisibility.js';

import AddToFavourites from './components/AddToFavourites.vue';
import AlreadyEnquired from './components/AlreadyEnquired.vue';
import EnquiryForm from './components/EnquiryForm/EnquiryForm.vue';
import EnquiryFormBottom from './components/EnquiryFormBottom.vue';
import EnquiryFormHeader from './components/EnquiryFormHeader/EnquiryFormHeader.vue';
import EnquiryFormModal from './components/EnquiryFormModal.vue';
import EnquiryPricingBar from './components/EnquiryPricingBar.vue';
import EnquirySentModal from './components/EnquirySentModal.vue';
import EnquirySentWelcomeBackModal from './components/EnquirySentWelcomeBackModal.vue';
import HasLeadHubOffer from './components/HasLeadHubOffer.vue';
import MapModal from './components/MapModal.vue';
import MobileBottomButton from './components/MobileBottomButton/MobileBottomButton.vue';
import MobileStartEnquiryBtn from './components/MobileStartEnquiryBtn.vue';
import PricingDetailsModal from './components/PricingDetailsModal/PricingDetailsModal.vue';
import ReviewModal from './components/ReviewModal/ReviewModal.vue';
import RoomAddOnModal from './components/RoomAddOnModal/RoomAddOnModal.vue';
import RoomMenuModal from './components/RoomMenuModal/RoomMenuModal.vue';
import SuspensionWarning from './components/SuspensionWarning.vue';
import WishlistModals from './components/WishlistModals.vue';
import initFlyingSidebar from './flyingEnquiryFormSidebar.js';
import { parseFormToBackend } from './misc/parsePayload';

export default {
  name: 'EnquiryFormWrapper',
  mixins: [ScreenWidth],
  components: {
    HasLeadHubOffer,
    WishlistModals,
    EnquirySentModal,
    EnquirySentWelcomeBackModal,
    BaseButton,
    EnquiryFormBottom,
    EnquiryPricingBar,
    SuspensionWarning,
    MobileStartEnquiryBtn,
    AlreadyEnquired,
    MobileBottomButton,
    EnquiryFormModal,
    EnquiryForm,
    EnquiryFormHeader,
    AddToFavourites,
    PageTile,
    ReviewModal,
    RoomAddOnModal,
    RoomMenuModal,
    PricingDetailsModal,
    MapModal,
  },
  data() {
    return {
      isLoading: true,
      isBelowForm: false,
      elementVisibility: null,
      messageHostFirstInteractionTracked: false,
      modalTriggeredFromUrl: false,
    };
  },

  computed: {
    attachFormTarget() {
      if (this.isBigDesktop) {
        return '#desktop-enquiry-form';
      }
      return '#modal-enquiry-form';
    },
    showMobileButtons() {
      return this.isBelowForm && !this.isBigDesktop;
    },
  },

  watch: {
    isLoading(isLoading) {
      if (isLoading) {
        return;
      }
      this.$nextTick(() => {
        document.getElementById('enquiry-form-static')?.remove();
      });
      App.es6.shared.waitFor(() => {
        return this.$refs.enquiryForm;
      }, () => {
        this.elementVisibility = new ElementVisibility(this.$refs.enquiryForm, ({ isBelow }) => {
          this.isBelowForm = isBelow;
        });
      });

      this.$nextTick(() => {
        this.trackFirstInteraction();
      });
    },

    isBigDesktop: {
      handler(isBigDesktop) {
        this.$store.commit('setBigDesktop', isBigDesktop);
        if (isBigDesktop) {
          this.$store.commit('setIsEnquiryFormModalOpen', false);
        }
      },
      immediate: true,
    },
    '$store.state.enquiryFormReady': {
      handler() {
        const isRtbFlowPossible = this.$store.getters.isRtbFlowPossible;
        if (isRtbFlowPossible !== null && !this.modalTriggeredFromUrl) {
          this.initOpenModalByUrl();
        }
      },
    },
  },

  methods: {
    openModal({ requestToBook, requestToBookMobilePath = null }) {
      if (
        (requestToBook === true && requestToBookMobilePath === true)
        || (requestToBook === false && requestToBookMobilePath === null && this.$store.getters.isRtbFlowPossible)
      ) {
        setContextParam('UserCabinet', 'room_page');
      }

      this.$store.commit('setIsEnquiryFormModalOpen', true);
      let label = 'modal-open';
      if (this.$store.getters.isRtbFlowPossible) {
        this.$store.commit('setRequestToBookPath', requestToBook);
        this.$store.commit('setRequestToBookMobilePath', requestToBookMobilePath);
        label = requestToBook ? 'open-request-to-book' : 'open-message-host';
      }

      this.$store.dispatch('trackEnquiryFormNavigation', {
        label,
      });
    },

    navigateToEnquiryForm() {
      this.openModal({ requestToBook: false });
    },

    trackFirstInteraction() {
      const handler = (event) => {
        const name = event.target.getAttribute('data-test-id');
        if (name) {
          this.$store.dispatch('trackEnquiryFormFirstInteraction', name);
          this.$refs.enquiryForm.removeEventListener('mousedown', handler);
        }
      };
      // Click is buggy on Chrome on html select fields, so I use mousedown
      this.$refs.enquiryForm.addEventListener('mousedown', handler);
    },
    messageHostBtnClicked() {
      this.$store.dispatch('trackGA4Interaction', {
        section: this.isBigDesktop ? 'event_info' : 'mobile_widget',
        element: 'message_host_button',
        value: null,
        interaction: 'click',
      });

      if (!this.messageHostFirstInteractionTracked) {
        this.$store.dispatch('trackEnquiryFormFirstInteraction', 'message-host-btn');
        this.messageHostFirstInteractionTracked = true;
      }

      this.openModal({ requestToBook: false });
    },
    initTabVisibilityHandler() {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.$store.dispatch('getManagerLastActivityOnline');
          this.$store.dispatch('fetchLastRfpData');
        } else {
          this.saveFormData();
        }
      });
      // back forward cache listener
      window.addEventListener('pageshow', () => {
        this.$store.dispatch('fetchLastRfpData');
      });
    },
    saveFormData() {
      const formDataKeeper = App.es6.views.quick_estimate.form_data_keeper();
      const state = this.$store.state;

      const payload = parseFormToBackend({
        form: state.form,
        tags: state.eventTypes,
        l10n: state.l10n,
        enquiry_comment: state.form.messageAndCatering.message,
      });

      formDataKeeper.mergeData(payload);
    },
    initUnloadHandler() {
      window.addEventListener('beforeunload', () => {
        this.saveFormData();
      });
    },
    onTrackReviewsModal(data) {
      App.track.event('room_page_interaction', data);
    },
    initOpenModalByUrl() {
      if (this.modalTriggeredFromUrl) {
        return;
      }
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('message_host') === '1' && this.$store.getters.isRtbFlowPossible) {
        this.openModal({ requestToBook: false });
        this.modalTriggeredFromUrl = true;
      }
    },
  },

  beforeCreate() {
    this.$store.dispatch('init')
      .then(() => {
        this.isLoading = false;
        this.$store.dispatch('openPricingForDate');
      });
  },

  mounted() {
    this.initUnloadHandler();

    $.subscribe('wishList:added_from_url', () => {
      this.$store.commit('wishlist/setIsRoomFavourite', true);
    });

    $.subscribe('loginState:change', () => {
      this.$store.dispatch('fetchUser', { fresh: true });
    });

    window.addEventListener('load', () => {
      window.gtag('get', window.__GA4_MEASURMENT_ID, 'client_id', clientId => {
        window.$store.commit('setAnalyticsClientId', clientId);
      });
    });

    initFlyingSidebar();

    this.$store.dispatch('getManagerLastActivityOnline');
    this.initTabVisibilityHandler();

    window.enquiryNow = () => {
      this.openModal({ requestToBook: false });
    };
  },
};
</script>
